import { useId, type InputHTMLAttributes } from 'react'
import Box from '@mui/material/Box'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  errorTextResponseLimits,
  sanitizePositiveIntegerDigits,
  type ChoiceTextResponseConfig,
} from '@/features/instruments/domain'
import { DISPLAY_SWITCH_SX } from './displaySettingSwitchSx'

/** Figma 309:3101 — 44×24 switch + "Add Text" (Inter 14, #4a5565). */
const ADD_TEXT_LABEL_SX = {
  m: 0,
  gap: 1,
  flexShrink: 0,
  '& .MuiFormControlLabel-label': {
    fontSize: '14px',
    lineHeight: '20px',
    color: '#4a5565',
    whiteSpace: 'nowrap',
  },
  '& .MuiFormControlLabel-label.Mui-disabled': { color: '#4a5565', opacity: 0.5 },
} as const

/** Figma 310:3154 — light blue settings card. */
const TEXT_INPUT_SETTINGS_PANEL_SX = {
  bgcolor: '#cfeeff',
  borderRadius: '8px',
  p: 2,
  mt: 2,
} as const

const PANEL_TITLE_SX = {
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 600,
  color: '#0f172a',
  m: 0,
} as const

const PANEL_DESCRIPTION_SX = {
  fontSize: '14px',
  lineHeight: '20px',
  color: '#64748b',
  mt: 0.5,
  mb: 2,
} as const

const PANEL_FIELD_LABEL_SX = {
  display: 'block',
  fontSize: '14px',
  lineHeight: '20px',
  color: '#64748b',
  mb: 1,
} as const

/** Figma 310:3166 — 46px white input, #e2e8f0 border, 10px radius, #9ca3af placeholder. */
const PANEL_INPUT_SX = {
  width: '100%',
  '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#ffffff' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
  '& .MuiOutlinedInput-input': {
    py: '12px',
    px: '16px',
    fontSize: '14px',
    lineHeight: '20px',
  },
  '& .MuiOutlinedInput-input::placeholder': { color: '#9ca3af', opacity: 1 },
} as const

interface AddTextSwitchProps {
  checked: boolean
  disabled: boolean
  onChange: (on: boolean) => void
  /** Accessible name suffix, e.g. the option label. */
  optionName: string
  testId: string
}

export function AddTextSwitch({ checked, disabled, onChange, optionName, testId }: Readonly<AddTextSwitchProps>) {
  return (
    <FormControlLabel
      disabled={disabled}
      control={
        <Switch
          checked={checked}
          disabled={disabled}
          onChange={(_, v) => onChange(v)}
          sx={DISPLAY_SWITCH_SX}
          inputProps={
            {
              'aria-label': `Add Text for ${optionName}`,
              'data-testid': testId,
            } as InputHTMLAttributes<HTMLInputElement>
          }
        />
      }
      label="Add Text"
      sx={ADD_TEXT_LABEL_SX}
    />
  )
}

interface TextInputSettingsPanelProps {
  value: ChoiceTextResponseConfig
  optionLabel: string
  onChange: (next: ChoiceTextResponseConfig) => void
}

export function TextInputSettingsPanel({ value, optionLabel, onChange }: Readonly<TextInputSettingsPanelProps>) {
  const baseId = useId()
  const minId = `${baseId}-min-characters`
  const maxId = `${baseId}-max-characters`
  const err = errorTextResponseLimits(value)
  return (
    <Box data-testid="text-input-settings-panel" sx={TEXT_INPUT_SETTINGS_PANEL_SX}>
      <Typography component="h3" sx={PANEL_TITLE_SX}>
        Text Input Settings
      </Typography>
      <Typography sx={PANEL_DESCRIPTION_SX}>
        Configure the text box shown to respondents when they select “{optionLabel}”.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Box sx={{ flex: 1 }}>
          <Typography component="label" htmlFor={minId} sx={PANEL_FIELD_LABEL_SX}>
            Min Characters
          </Typography>
          <TextField
            id={minId}
            fullWidth
            hiddenLabel
            placeholder="Eg:5"
            value={value.minCharacters}
            onChange={(e) =>
              onChange({ ...value, minCharacters: sanitizePositiveIntegerDigits(e.target.value) })
            }
            error={Boolean(err)}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', 'data-testid': 'text-input-min-characters' }}
            sx={PANEL_INPUT_SX}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography component="label" htmlFor={maxId} sx={PANEL_FIELD_LABEL_SX}>
            Max Characters
          </Typography>
          <TextField
            id={maxId}
            fullWidth
            hiddenLabel
            placeholder="Eg:100"
            value={value.maxCharacters}
            onChange={(e) =>
              onChange({ ...value, maxCharacters: sanitizePositiveIntegerDigits(e.target.value) })
            }
            error={Boolean(err)}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', 'data-testid': 'text-input-max-characters' }}
            sx={PANEL_INPUT_SX}
          />
        </Box>
      </Stack>
      {err ? (
        <FormHelperText error sx={{ mx: 0 }} data-testid="text-input-settings-error">
          {err}
        </FormHelperText>
      ) : null}
    </Box>
  )
}
